import { API_BASE_URL } from '../../config'
import { useBookingStore } from '../../store'

export interface ApiError {
  status: number
  message: string
  /** Stable machine-readable code from the error body (e.g. `no_available_spots`), when the API sends one. */
  errorCode?: string
}

const readErrorCode = async (
  response: Response,
): Promise<string | undefined> => {
  try {
    const body = (await response.json()) as { errorCode?: unknown }
    return typeof body.errorCode === 'string' ? body.errorCode : undefined
  } catch {
    // Empty or non-JSON error body.
    return undefined
  }
}

export const apiFetch = async <T>(
  path: string,
  options?: RequestInit,
): Promise<T> => {
  const { env } = useBookingStore.getState()
  const baseUrl = API_BASE_URL[env]
  const url = `${baseUrl}/${path.replace(/^\//, '')}`

  // `headers` after the spread, not before: spreading `options` last would
  // replace the merged object with the caller's own `headers` and drop
  // Content-Type along with it.
  //
  // Content-Type only when there is a body to describe. `application/json` is
  // not a CORS-safelisted value, so putting it on a bodyless GET adds a
  // preflight requirement for `content-type` and buys nothing. The legacy tool
  // sent a bare `{ authorization }` on its authorized reads and
  // `{ 'content-type': 'application/json' }` only on POST/PUT/DELETE —
  // see booking-tool/src/global/network/network.ts.
  const hasBody = options?.body != null

  const response = await fetch(url, {
    ...options,
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...options?.headers,
    },
  })

  if (!response.ok) {
    const error: ApiError = {
      status: response.status,
      message: `Request failed: ${response.status} ${response.statusText}`,
      errorCode: await readErrorCode(response),
    }
    throw error
  }

  return response.json() as Promise<T>
}
